import React, { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { SectionCard } from "../../components/Common";
import { Lock, Plus, Printer, ShieldCheck } from "lucide-react";

function DischargeAddendumBox({ admissionId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!text.trim()) { toast.error("Addendum cannot be empty"); return; }
    try {
      setSaving(true);
      await api.post(`/admissions/${admissionId}/discharge/addendum`, { text: text.trim() });
      toast.success("Addendum appended to discharge summary");
      setText(""); setOpen(false); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to add addendum"); }
    finally { setSaving(false); }
  };

  if (!open) return (
    <button data-testid="disc-addendum-open" onClick={() => setOpen(true)} className="ths-print-hide h-8 px-3 rounded-lg text-xs font-medium border border-slate-200 hover:bg-slate-50 flex items-center gap-1"><Plus className="w-3 h-3" /> Add addendum</button>
  );
  return (
    <div className="ths-print-hide space-y-2" data-testid="disc-addendum-editor">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" placeholder="Correction or note to append (does not modify the signed summary)…" data-testid="disc-addendum-text" />
      <div className="flex items-center justify-end gap-2">
        <button onClick={() => { setOpen(false); setText(""); }} className="h-8 px-3 rounded-lg text-xs text-slate-600 hover:bg-slate-100">Cancel</button>
        <button data-testid="disc-addendum-save" disabled={saving} onClick={submit} className="h-8 px-3 rounded-lg text-xs font-medium bg-[#1976D2] text-white disabled:opacity-60">{saving ? "Saving…" : "Append"}</button>
      </div>
    </div>
  );
}

export function DischargeSummaryView({ admission, canAddendum, onAddendum }) {
  const d = admission.discharge || {};
  const meds = d.discharge_meds || [];
  const addenda = d.addenda || [];
  const admittedAt = admission.admitted_at ? new Date(admission.admitted_at) : null;
  const dischargedAt = d.discharged_at ? new Date(d.discharged_at) : (admission.discharged_at ? new Date(admission.discharged_at) : null);

  const print = () => window.print();

  return (
    <SectionCard
      testId="discharge-summary-section"
      title={<span className="flex items-center gap-2"><Lock className="w-4 h-4 text-[#1B5E20]" /> Discharge Summary <span className="chip chip-green ml-1 !inline-flex">Signed · locked</span></span>}
      actions={
        <div className="flex items-center gap-2 ths-print-hide">
          <button data-testid="disc-print" onClick={print} className="h-8 px-3 rounded-lg text-xs font-medium bg-[#1976D2] text-white hover:bg-[#1565C0] flex items-center gap-1.5"><Printer className="w-3.5 h-3.5" /> Print</button>
        </div>
      }
    >
      <div className="ths-print-area p-6 md:p-8 space-y-5 text-slate-900" data-testid="discharge-summary-print">
        <div className="border-b border-slate-300 pb-4">
          <div className="text-xs uppercase tracking-widest text-slate-500">Vitality Medical Software</div>
          <div className="text-2xl font-semibold mt-1">Discharge Summary</div>
          <div className="text-xs text-slate-500 mt-1">Admission {admission.admission_no}</div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><div className="text-[11px] uppercase text-slate-500">Patient</div><div className="font-semibold" data-testid="ds-patient-name">{admission.patient_name}</div></div>
          <div><div className="text-[11px] uppercase text-slate-500">Patient ID</div><div className="tabular">{admission.patient_ths_id}</div></div>
          <div><div className="text-[11px] uppercase text-slate-500">Ward / Bed</div><div>{admission.ward}{admission.bed_no ? ` · ${admission.bed_no}` : ""}</div></div>
          <div><div className="text-[11px] uppercase text-slate-500">Condition</div><div className="font-semibold" data-testid="ds-condition">{d.condition || "—"}</div></div>
          <div><div className="text-[11px] uppercase text-slate-500">Admitted</div><div>{admittedAt ? admittedAt.toLocaleDateString() : "—"}</div></div>
          <div><div className="text-[11px] uppercase text-slate-500">Discharged</div><div>{dischargedAt ? dischargedAt.toLocaleDateString() : "—"}</div></div>
          <div><div className="text-[11px] uppercase text-slate-500">Length of stay</div><div>{admittedAt && dischargedAt ? Math.max(1, Math.round((dischargedAt - admittedAt) / 86400000)) + " day(s)" : "—"}</div></div>
          <div><div className="text-[11px] uppercase text-slate-500">Review date</div><div data-testid="ds-review-date">{d.follow_up_date || "—"}</div></div>
        </div>

        <div>
          <div className="text-[11px] uppercase text-slate-500">Discharge diagnosis</div>
          <div className="mt-1 text-sm" data-testid="ds-diagnosis">{d.discharge_diagnosis || "—"}</div>
        </div>
        {d.treatment_given && (
          <div>
            <div className="text-[11px] uppercase text-slate-500">Treatment given</div>
            <div className="mt-1 text-sm whitespace-pre-line">{d.treatment_given}</div>
          </div>
        )}

        <div>
          <div className="text-[11px] uppercase text-slate-500 mb-1">Discharge medications</div>
          {meds.length === 0 ? (
            <div className="text-sm text-slate-500">None</div>
          ) : (
            <table className="w-full text-sm border border-slate-300" data-testid="ds-meds-table">
              <thead className="bg-slate-100 text-left text-[11px] uppercase text-slate-600">
                <tr><th className="px-3 py-2 border-b border-slate-300">Drug</th><th className="px-3 py-2 border-b border-slate-300">Dose</th><th className="px-3 py-2 border-b border-slate-300">Route</th><th className="px-3 py-2 border-b border-slate-300">Frequency</th><th className="px-3 py-2 border-b border-slate-300">Duration</th><th className="px-3 py-2 border-b border-slate-300">Qty</th></tr>
              </thead>
              <tbody>
                {meds.map((m, i) => (
                  <tr key={i} className="border-b border-slate-200 last:border-0">
                    <td className="px-3 py-2 font-medium">{m.drug}</td>
                    <td className="px-3 py-2">{m.dose || "—"}</td>
                    <td className="px-3 py-2">{m.route || "—"}</td>
                    <td className="px-3 py-2">{m.frequency || "—"}</td>
                    <td className="px-3 py-2">{m.duration || "—"}</td>
                    <td className="px-3 py-2">{m.quantity || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div>
          <div className="text-[11px] uppercase text-slate-500">Discharge summary &amp; follow-up instructions</div>
          <div className="mt-1 text-sm whitespace-pre-line" data-testid="ds-summary">{d.summary || "—"}</div>
        </div>
        {d.follow_up_notes && (
          <div>
            <div className="text-[11px] uppercase text-slate-500">Follow-up notes</div>
            <div className="mt-1 text-sm whitespace-pre-line">{d.follow_up_notes}</div>
          </div>
        )}

        {addenda.length > 0 && (
          <div>
            <div className="text-[11px] uppercase text-slate-500 mb-1">Addenda</div>
            <ul className="space-y-2">
              {addenda.map((a, i) => (
                <li key={i} className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm">
                  <div className="whitespace-pre-line">{a.text}</div>
                  <div className="text-[11px] text-slate-500 mt-1">— {a.by} · {new Date(a.at).toLocaleString()}</div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="pt-4 border-t border-slate-300 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-[11px] uppercase text-slate-500">Discharging doctor</div>
            <div className="mt-1 text-sm font-semibold" data-testid="ds-signed-by">{d.discharged_by || admission.discharged_by || "—"}</div>
            <div className="text-xs text-slate-500 mt-0.5">Signed electronically</div>
            <div className="text-xs text-slate-500">{dischargedAt ? dischargedAt.toLocaleString() : ""}</div>
          </div>
          <div className="flex items-end justify-end">
            <span className="chip chip-green flex items-center gap-1 !inline-flex"><ShieldCheck className="w-3 h-3" /> Signed &amp; locked</span>
          </div>
        </div>
      </div>

      {canAddendum && (
        <div className="ths-print-hide px-5 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-slate-500">Corrections must be added as an addendum — the signed summary above cannot be edited.</div>
          <DischargeAddendumBox admissionId={admission.admission_id} onSaved={onAddendum} />
        </div>
      )}
    </SectionCard>
  );
}
